// The improved version of the My Intrivity Application
import { html, css, LitElement, svg } from 'lit-element';
import { unsafeHTML } from 'lit-html/directives/unsafe-html.js';
import { clone, cloneDeep } from 'lodash-es';
import { library, dom } from "@fortawesome/fontawesome-svg-core";   
 
import { doc, getDoc } from 'firebase/firestore';

import './badge.js';
import './domain_app.js';

import { db } from './intrivity_app.js';
import {
    emitEvent, getScore, groupMemberDisplayName, INTRIVITY_NAV_SELECT
} from './utility.js'
import { a11yStyles, accessibleTextColor, ON_DOMAIN_TEXT } from './a11y.js';
import { t, Localized } from './i18n.js';

export const showNodeText = function(event) {
        //alert(domian.label[level]);
        console.log(event);
};
    
export class IntrivityProfileApp extends Localized(LitElement) {

    static get styles() {
        return [a11yStyles, css`
            .teams { 
                max-width: 600px; margin: auto;
            }
            .group_list {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
                grid-gap: 10px;
            }
            .group_badge {
                display: grid;
                grid-template-columns: 60px 1fr;
            }
            .group_profile {
                display: grid;
                grid-template-columns: 30px 10px 1fr;
            }        
            /* The domain images and score tiles were clickable <div>/<img>.
               They are buttons now so they are reachable by keyboard and
               announce their expanded state (WCAG 2.1.1, 4.1.2). */
            .domain-image-button {
                line-height: 0;
            }
            .domain-tile {
                display: block;
                width: 100%;
            }
            .cta {
                padding: 5px 15px;
                font-weight: bolder;
                display: inline-block;
                /* Darkened from rgb(87,130,133): white on that was 4.25:1. */
                background-color: rgb(80, 120, 123);
                color: white;
                margin: 0 4px;
            }
        `];
    }
    
    static get properties() {
        return {
            selected: { type: String },
            space_domain: { type: Object },
            message_domain: { type: Object },
            relationship_domain: { type: Object },
            time_domain: { type: Object },   
            group_report: { type: Object },
            report: { type: Object },
            user_profile: { type: Object },
            printable: { type: Boolean },           // printable view
            size: { type: Number },
            _disableReportPrivacy: { type: Boolean }
        };
    }

    constructor() {
        super();
        this.size = 1;
        this._disableReportPrivacy = false;
        this.getDomain = this.getDomain.bind(this);
        this.getSVGReport = this.getSVGReport.bind(this);
    }

    // Read live from Firestore rather than the (possibly stale) cached
    // report JSON, so toggling the group/team's privacy setting takes
    // effect immediately without needing the report reprocessed.
    async updated(changedProps) {
        if (changedProps.has('group_report') && this.group_report && this.group_report.group) {
            const groupId = this.group_report.group;
            // Fail closed immediately on switch, and guard against a
            // slower fetch for a since-abandoned team resolving after a
            // newer one and clobbering it with stale data.
            this._disableReportPrivacy = false;
            try {
                const snap = await getDoc(doc(db, 'groups', groupId));
                if (this.group_report && this.group_report.group === groupId) {
                    this._disableReportPrivacy = snap.exists() && !!snap.data().disable_report_privacy;
                }
            } catch (e) {
                console.warn('Could not load report privacy setting:', e);
            }
        }
    }

    getDomain(domain) {

        this.selected = domain;

    }
    
    getSVGReport(group_report) {
        let rep = { "m": {}, "r": {}, "s": {}, "t": {} };
        group_report.users.map((user) => {
            let m = getScore(user.profile.m);
            let r = getScore(user.profile.r);
            let s = getScore(user.profile.s);
            let t = getScore(user.profile.t);
            if (rep.m.hasOwnProperty(m)) {
                rep.m[m] = rep.m[m] + 1;
            } else {
                rep.m[m] = 1;
            }
            if (rep.r.hasOwnProperty(r)) {
                rep.r[r] = rep.r[r] + 1;
            } else {
                rep.r[r] = 1;
            }
            if (rep.s.hasOwnProperty(s)) {
                rep.s[s] = rep.s[s] + 1;
            } else {
                rep.s[s] = 1;
            }
            if (rep.t.hasOwnProperty(t)) {
                rep.t[t] = rep.t[t] + 1;
            } else {
                rep.t[t] = 1;
            }
        });
        return rep;
    }
    
    showNodeText(event) {
        //alert(domian.label[level]);
        console.log(event);
    }
    

    showDomainText(data, message) {
        const errorMsg = message || t('common.required');
        const errorElem = document.createElement('div');
        errorElem.style.backgroundColor = '#ff0000';
        errorElem.innerHTML = errorMsg;
        errorElem.style.top = `${(data && data.position && data.position.posy) ? data.position.posy : '0'}px`;
        errorElem.style.left = `${(data && data.position && data.position.posx) ? data.position.posx : '0'}px`;
        errorElem.style.position = 'absolute';
        errorElem.zIndex = 900;

        document.body.appendChild(errorElem);
    }
    
    // Selecting a domain expands its detail panel below; selecting the active
    // one collapses it again. In the collapsed branch `this.selected` is always
    // falsy, so this matches the previous behaviour exactly.
    _toggleDomain(key) {
        if (this.selected === key) { this.selected = undefined; } else { this.getDomain(key); }
    }

    // The domain glyph and the score tile are two controls onto the same panel;
    // both get a name and the shared expanded state.
    _domainImageButton(key, domain, height, imgStyle = '') {
        return html`
            <button
                type="button"
                class="unstyled-button domain-image-button"
                aria-expanded=${this.selected === key ? 'true' : 'false'}
                @click=${() => this._toggleDomain(key)}>
                <img alt="${t('profile.domainImageAlt', { domain: domain.name })}" height="${height}" style="${imgStyle}" src="./images/${domain.image}">
            </button>
        `;
    }

    render() {
        return html`

            <h1 class="sr-only">${t('profile.heading')}</h1>

            <div style="margin-top: 50px;  width: 100%;">

                <div style="max-width: 800px; margin: auto; display: grid; grid-gap: 0px; grid-template-columns: 1fr 4fr 8fr 4fr 1fr;">

                    <div></div>
                    <div style="grid-column: 2 / span 4;">
                        ${this._domainImageButton('message', this.message_domain, '75px')}
                    </div>

                    ${this.selected ? html`
            
                        <div style="display: flex; align-items: flex-end;">
                            ${this._domainImageButton('relationship', this.relationship_domain, '75px', 'margin-right: 10px;')}
                        </div>
        
                        ${this.selected === "message" ? html`
                            <intrivity-domain style="grid-column: 2 / span 3;" raw="${this.report.m.average}" .domain=${this.message_domain}></intrivity-domain>
                        `: html``}
        
                        ${this.selected === "relationship" ? html`
                            <intrivity-domain style="grid-column: 2 / span 3;" raw="${this.report.r.average}" .domain=${this.relationship_domain}></intrivity-domain>
                        `: html``}
        
                        ${this.selected === "space" ? html`
                            <intrivity-domain style="grid-column: 2 / span 3;" raw="${this.report.s.average}" .domain=${this.space_domain}></intrivity-domain>
                        `: html``}
        
                        ${this.selected === "time" ? html`
                            <intrivity-domain style="grid-column: 2 / span 3;" raw="${this.report.t.average}" .domain=${this.time_domain}></intrivity-domain>
                        `: html``}
        
        
                        <div>
                            ${this._domainImageButton('space', this.space_domain, '70px')}
                        </div>



                    `: html`
                        <div></div>
                        <button type="button" class="unstyled-button domain-tile" aria-expanded=${this.selected === 'message' ? 'true' : 'false'} @click=${() => this._toggleDomain('message')} style="border: 10px solid ${this.message_domain.color}; display: flex; align-items: flex-end; aspect-ratio: 1; height: 150px; color: ${accessibleTextColor(this.message_domain.color)};">
                            <div style="font-size: 22px; margin: 10px; width: 100%; text-align: center;">
                                <div style="margin-bottom: 20%; font-size: 34px; font-weight: bolder;">${getScore(this.report.m.average)}</div>
                                <div style="font-size: 22px; width: 100%; text-align: right;">${t('domain.message.label')}</div>
                            </div>
                        </button>
                        <div style="display: flex; align-items: flex-end; color: ${ON_DOMAIN_TEXT}; background-color: ${this.message_domain.color};">
                            <div style="margin: 10px; width: 50%;">${t('domain.message.summary')}</div>
                        </div>
                        <button type="button" class="unstyled-button domain-tile" aria-expanded=${this.selected === 'relationship' ? 'true' : 'false'} @click=${() => this._toggleDomain('relationship')} style="border: 10px solid ${this.relationship_domain.color}; display: flex; align-items: flex-end; aspect-ratio: 1; height: 150px; color: ${accessibleTextColor(this.relationship_domain.color)};">
                            <div style="font-size: 22px; margin: 10px; width: 100%; text-align: center;">
                                <div style="margin-bottom: 20%; font-size: 34px; width: 100%; font-weight: bolder; text-align: center;">${getScore(this.report.r.average)}</div>
                                <div style="font-size: 22px; width: 100%; text-align: left;">${t('domain.relationship.label')}</div>
                            </div>                        
                        </button>
                        <div>${this._domainImageButton('relationship', this.relationship_domain, '70px')}</div>

                        <div></div>
                        <div style="display: flex; align-items: flex-end; text-align: right; color: ${ON_DOMAIN_TEXT}; background-color: ${this.space_domain.color};">
                            <div style="margin: 10px;">${t('domain.space.summary')}</div>
                        </div>
                        <div style="text-align: center; font-size: 22px; margin: auto; aspect-ratio: 1;">
                            ${this.group_report ? html`
                            <svg-profile .maxnum=${this.group_report.num} .rep=${this.getSVGReport(this.group_report)} .report=${this.report} .message_domain=${this.message_domain} .time_domain=${this.time_domain} .relationship_domain=${this.relationship_domain} .space_domain=${this.space_domain}></svg-profile>    
                            `: html``}
                        </div>
                        <div style="color: ${ON_DOMAIN_TEXT}; background-color: ${this.relationship_domain.color};">
                            <div style="margin: 10px;">${t('domain.relationship.summary')}</div>
                        </div>
                        <div></div>

                        <div style="display: flex; align-items: flex-end;">
                            ${this._domainImageButton('space', this.space_domain, '75px', 'margin-right: 10px;')}
                        </div>
                        <button type="button" class="unstyled-button domain-tile" aria-expanded=${this.selected === 'space' ? 'true' : 'false'} @click=${() => this._toggleDomain('space')} style="border: 10px solid ${this.space_domain.color}; aspect-ratio: 1; height: 150px; color: ${accessibleTextColor(this.space_domain.color)};">
                            <div style="font-size: 22px; margin: 10px; text-align: center;">
                                <div style="font-size: 22px; text-align: right; width: 100%;">${t('domain.space.label')}</div>
                                <div style="margin-top: 20%; font-size: 34px; font-weight: bolder;">${getScore(this.report.s.average)}</div>
                            </div>               
                        </button>
                        <div style="width: 100%; text-align: right; color: ${ON_DOMAIN_TEXT}; background-color: ${this.time_domain.color};">
                            <div style="float: right; margin: 10px; width: 60%;">${t('domain.time.summary')}</div>
                        </div>
                        <button type="button" class="unstyled-button domain-tile" aria-expanded=${this.selected === 'time' ? 'true' : 'false'} @click=${() => this._toggleDomain('time')} style="border: 10px solid ${this.time_domain.color}; aspect-ratio: 1; height: 150px; color: ${accessibleTextColor(this.time_domain.color)};">
                            <div style="font-size: 22px; margin: 10px; text-align: center;">
                                <div style="font-size: 22px; text-align: left; width: 100%;">${t('domain.time.label')}</div>
                                <div style="margin-top: 20%; font-size: 34px; font-weight: bolder;">${getScore(this.report.t.average)}</div>
                            </div>
                        </button>
                        <div></div>
                    `}
        

                    <div style="grid-column: 1 / span 4; text-align: right;">
                        ${this._domainImageButton('time', this.time_domain, '65px')}
                    </div>
                    <div></div>

                </div>       
        
                <div style="max-width: 800px; margin: auto; display: grid; grid-gap: 0px; grid-template-columns: 1fr 4fr 8fr 4fr 1fr;">
                                <div></div>
                                <div></div>
                                <div style="text-align: center; font-size: 22px; margin: auto; ">   
                                   ${this.group_report ? html`<svg-legend .rep=${this.getSVGReport(this.group_report)} .maxnum=${this.group_report.num}></svg-legend>` : html``}
                                </div>
                                <div></div>
                                <div></div>
                </div>

                ${this.selected ? html`
                    <div style="padding: 20px;"></div>
                    <div style="width: 100%; text-align: center;">
                        <button type="button" class="unstyled-button cta" @click=${(e)=> {
                            emitEvent(INTRIVITY_NAV_SELECT, { "selection": this.selected }, this);
                        }}>${t('profile.goToDetails')}</button>
                        <button type="button" class="unstyled-button cta" @click=${(e)=>{ this.selected = undefined; this.requestUpdate();}}>${t('profile.backToSummary')}</button>     
                    </div>
                `: html``}
        
                <div class="teams">
                ${this.group_report ? html`

                    <h3>${this.group_report.title}</h3>

                    <div class="group_list">

                        ${this.group_report.users.sort((a, b) => {
                            if (a.lastName + ', ' + a.firstName < b.lastName + ', ' + b.firstName) {
                              return -1;
                            }
                            if (a.lastName + ', ' + a.firstName > b.lastName + ', ' + b.firstName) {
                              return 1;
                            }
                            return 0;
}                       ).map((user, i)=> html`

                            ${this.selected === "message" ? html`
                                <div class="group_profile">
                                       <intrivity-domain profile="true" raw="${getScore(user.profile.m)}" .domain=${this.message_domain}></intrivity-domain>
                                       <div style="color: ${this.message_domain.color}">${getScore(user.profile.m)}</div>
                                       <div style="margin-left: 10px;">${groupMemberDisplayName(user, i, this._disableReportPrivacy, this.user_profile && this.user_profile.email)}</div>
                               </div>
                            `: html``}

                            ${this.selected === "relationship" ? html`
                                <div class="group_profile">
                                       <intrivity-domain profile="true" raw="${getScore(user.profile.r)}" .domain=${this.relationship_domain}></intrivity-domain>
                                       <div style="color: ${this.relationship_domain.color}">${getScore(user.profile.r)}</div>
                                       <div style="margin-left: 10px;">${groupMemberDisplayName(user, i, this._disableReportPrivacy, this.user_profile && this.user_profile.email)}</div>
                               </div>
                            `: html``}

                            ${this.selected === "space" ? html`
                                <div class="group_profile">
                                       <intrivity-domain profile="true" raw="${getScore(user.profile.s)}" .domain=${this.space_domain}></intrivity-domain>
                                       <div style="color: ${this.space_domain.color}">${getScore(user.profile.s)}</div>
                                       <div style="margin-left: 10px;">${groupMemberDisplayName(user, i, this._disableReportPrivacy, this.user_profile && this.user_profile.email)}</div>
                               </div>
                            `: html``}

                            ${this.selected === "time" ? html`
                                <div class="group_profile">
                                       <intrivity-domain profile="true" raw="${getScore(user.profile.t)}" .domain=${this.time_domain}></intrivity-domain>
                                       <div style="color: ${this.time_domain.color}">${getScore(user.profile.t)}</div>
                                       <div style="margin-left: 10px;">${groupMemberDisplayName(user, i, this._disableReportPrivacy, this.user_profile && this.user_profile.email)}</div>
                               </div>
                            `: html``}
        
                            ${this.selected ? html``: html`

                                <div class="group_badge">
                                   <intrivity-badge-plain .m=${getScore(user.profile.m)} .r=${getScore(user.profile.r)} .s=${getScore(user.profile.s)} .t=${getScore(user.profile.t)} .size=${this.size}></intrivity-badge-plain>
                                   <div style="margin-left: 5px;">
                                       <div>${groupMemberDisplayName(user, i, this._disableReportPrivacy, this.user_profile && this.user_profile.email)}</div>
                                       <div style="font-size: smaller; text-align: center; display: inline-grid; grid-template-columns: 1fr 1fr 1fr 1fr;">
                                           <div>M</div><div>R</div><div>T</div><div>S</div>
                                           <div>${getScore(user.profile.m)}</div>
                                           <div>${getScore(user.profile.r)}</div>
                                           <div>${getScore(user.profile.t)}</div>
                                           <div>${getScore(user.profile.s)}</div>
                                       </div>
                                   </div>
                               </div>
        
                            `}
        
                        ` )}

                    </div>

                `: html``}
                </div>
        

            </div>
        
        `
    } 
 
}
if (!customElements.get('intrivity-profile')) customElements.define('intrivity-profile', IntrivityProfileApp);

export class SvgProfile extends Localized(LitElement) {
    

    static get styles() {
        return css`
            .teams { 
                max-width: 600px; margin: auto;
            }
            .group_list {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
                grid-gap: 10px;
            }
            .group_badge {
                display: grid;
                grid-template-columns: 60px 1fr;
            }
            .group_profile {
                display: grid;
                grid-template-columns: 30px 10px 1fr;
            }  
            .sr-only {
                position: absolute;
                width: 1px;
                height: 1px;
                padding: 0;
                margin: -1px;
                overflow: hidden;
                clip: rect(0, 0, 0, 0);
                white-space: nowrap;
                border: 0;
            }
            svg [role="button"]:not(:focus-visible) {
                outline: none;
            }
            svg [role="button"] {
                cursor: pointer;
            }
            svg [role="button"]:focus-visible {
                outline: 3px solid #1a1a1a;
                outline-offset: 2px;
            }
            #clickable-div {
                font-size: smaller;
                display: flex;
                align-items: center;
                justify-content: center;
                background-color: transparent;
                position: absolute;
                visibility: hidden; /* Initially hidden */
                font-size: 18px;
            }
        `;
    }
    
    static get properties() {
        return {
            rep: { type: Object },
            selected: { type: String },
            space_domain: { type: Object },
            message_domain: { type: Object },
            relationship_domain: { type: Object },
            time_domain: { type: Object }, 
            maxnum: { type: Number },
            report: { type: Object }
        };
    }

    constructor() {
        super(); 
        this.maxnum = 5;
    }
    
    connectedCallback() {
        super.connectedCallback();
        console.log(this.rep);
        console.log(this.report);
        console.log("M1: " + this.rep.m["3"]);
    }
    
    // get ratio-- ratio should be max of r=19-- so 16 is max value 1 is min where 16 is proportional to number of responses
    // we need max
    svgDomainPoint(domain, textvalue, label, color, x, y ) {
        if (this.rep[domain].hasOwnProperty(textvalue)) {
            const count = this.rep[domain][textvalue];
            return svg`<g
                    role="button"
                    tabindex="0"
                    aria-label="${label}: ${count} ${count === 1 ? 'person' : 'people'}"
                    @click="${(e)=> { this.showTextMessage(e, label);}}"
                    @keydown="${(e)=> { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.showTextMessage(e, label); } }}"
                ><circle cx="${x}" cy="${y}" r="${(3 + this.getSvgRatio(count))}" fill="${color}" /></g>`;
        } 
    }
    getSvgRatio(n) {
        if (this.maxnum > 5) {
            return (n/this.maxnum)*15;
        } else {
            return n;
        }
    }
    
    showTextMessage(event, message) {
        const div = this.shadowRoot.getElementById('clickable-div');
        div.innerText = message;
        div.style.left = event.pageX + 'px';
        div.style.top = event.pageY + 'px';
        div.style.visibility = 'visible'; // Make the div visible
    }
  
    svgDomainMyPoint(domain) {
        if (getScore(this.report[domain].average) === 1) {
            if (domain === "m") {
                return svg`<g role="button" tabindex="0" aria-label="${t('profile.yourScore', { label: this.message_domain.label[0] })}" @click="${(e)=> { this.showTextMessage(e, this.message_domain.label[0]);}}" @keydown="${(e)=> { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.showTextMessage(e, this.message_domain.label[0]); } }}"><circle cx="90" cy="90" r="2" fill="black" /></g>`;
            } else if (domain === "s") {
                return svg`<g role="button" tabindex="0" aria-label="${t('profile.yourScore', { label: this.space_domain.label[0] })}" @click="${(e)=> { this.showTextMessage(e, this.space_domain.label[0]);}}" @keydown="${(e)=> { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.showTextMessage(e, this.space_domain.label[0]); } }}"><circle cx="90" cy="110" r="2" fill="black" /></g>`;
            } else if (domain === "t") {
                return svg`<g role="button" tabindex="0" aria-label="${t('profile.yourScore', { label: this.time_domain.label[0] })}" @click="${(e)=> { this.showTextMessage(e, this.time_domain.label[0]);}}" @keydown="${(e)=> { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.showTextMessage(e, this.time_domain.label[0]); } }}"><circle cx="110" cy="110" r="2" fill="black" /></g>`;
            } else if (domain === "r") {
                return svg`<g role="button" tabindex="0" aria-label="${t('profile.yourScore', { label: this.relationship_domain.label[0] })}" @click="${(e)=> { this.showTextMessage(e, this.relationship_domain.label[0]);}}" @keydown="${(e)=> { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.showTextMessage(e, this.relationship_domain.label[0]); } }}"><circle cx="110" cy="90" r="2" fill="black" /></g>`;
            } 
        } else if (getScore(this.report[domain].average) === 2) {
            if (domain === "m") {
                return svg`<g role="button" tabindex="0" aria-label="${t('profile.yourScore', { label: this.message_domain.label[1] })}" @click="${(e)=> { this.showTextMessage(e, this.message_domain.label[1]);}}" @keydown="${(e)=> { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.showTextMessage(e, this.message_domain.label[1]); } }}"><circle cx="70" cy="70" r="2" fill="black" /></g>`;
            } else if (domain === "s") {
                return svg`<g role="button" tabindex="0" aria-label="${t('profile.yourScore', { label: this.space_domain.label[1] })}" @click="${(e)=> { this.showTextMessage(e, this.space_domain.label[1]);}}" @keydown="${(e)=> { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.showTextMessage(e, this.space_domain.label[1]); } }}"><circle cx="70" cy="130" r="2" fill="black" /></g>`;
            } else if (domain === "t") {
                return svg`<g role="button" tabindex="0" aria-label="${t('profile.yourScore', { label: this.time_domain.label[1] })}" @click="${(e)=> { this.showTextMessage(e, this.time_domain.label[1]);}}" @keydown="${(e)=> { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.showTextMessage(e, this.time_domain.label[1]); } }}"><circle cx="130" cy="130" r="2" fill="black" /></g>`;
            } else if (domain === "r") {
                return svg`<g role="button" tabindex="0" aria-label="${t('profile.yourScore', { label: this.relationship_domain.label[1] })}" @click="${(e)=> { this.showTextMessage(e, this.relationship_domain.label[1]);}}" @keydown="${(e)=> { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.showTextMessage(e, this.relationship_domain.label[1]); } }}"><circle cx="130" cy="70" r="2" fill="black" /></g>`;
            }            
        } else if (getScore(this.report[domain].average) === 3) {
            if (domain === "m") {
                return svg`<g role="button" tabindex="0" aria-label="${t('profile.yourScore', { label: this.message_domain.label[2] })}" @click="${(e)=> { this.showTextMessage(e, this.message_domain.label[2]);}}" @keydown="${(e)=> { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.showTextMessage(e, this.message_domain.label[2]); } }}"><circle cx="50" cy="50" r="2" fill="black" /></g>`;
            } else if (domain === "s") {
                return svg`<g role="button" tabindex="0" aria-label="${t('profile.yourScore', { label: this.space_domain.label[2] })}" @click="${(e)=> { this.showTextMessage(e, this.space_domain.label[2]);}}" @keydown="${(e)=> { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.showTextMessage(e, this.space_domain.label[2]); } }}"><circle cx="50" cy="150" r="2" fill="black" /></g>`;
            } else if (domain === "t") {
                return svg`<g role="button" tabindex="0" aria-label="${t('profile.yourScore', { label: this.time_domain.label[2] })}" @click="${(e)=> { this.showTextMessage(e, this.time_domain.label[2]);}}" @keydown="${(e)=> { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.showTextMessage(e, this.time_domain.label[2]); } }}"><circle cx="150" cy="150" r="2" fill="black" /></g>`;
            } else if (domain === "r") {
                return svg`<g role="button" tabindex="0" aria-label="${t('profile.yourScore', { label: this.relationship_domain.label[2] })}" @click="${(e)=> { this.showTextMessage(e, this.relationship_domain.label[2]);}}" @keydown="${(e)=> { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.showTextMessage(e, this.relationship_domain.label[2]); } }}"><circle cx="150" cy="50" r="2" fill="black" /></g>`;
            }
        } else if (getScore(this.report[domain].average) === 4) {
            if (domain === "m") {
                return svg`<g role="button" tabindex="0" aria-label="${t('profile.yourScore', { label: this.message_domain.label[3] })}" @click="${(e)=> { this.showTextMessage(e, this.message_domain.label[3]);}}" @keydown="${(e)=> { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.showTextMessage(e, this.message_domain.label[3]); } }}"><circle cx="30" cy="30" r="2" fill="black" /></g>`;
            } else if (domain === "s") {
                return svg`<g role="button" tabindex="0" aria-label="${t('profile.yourScore', { label: this.space_domain.label[3] })}" @click="${(e)=> { this.showTextMessage(e, this.space_domain.label[3]);}}" @keydown="${(e)=> { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.showTextMessage(e, this.space_domain.label[3]); } }}"><circle cx="30" cy="170" r="2" fill="black" /></g>`;
            } else if (domain === "t") {
                return svg`<g role="button" tabindex="0" aria-label="${t('profile.yourScore', { label: this.time_domain.label[3] })}" @click="${(e)=> { this.showTextMessage(e, this.time_domain.label[3]);}}" @keydown="${(e)=> { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.showTextMessage(e, this.time_domain.label[3]); } }}"><circle cx="170" cy="170" r="2" fill="black" /></g>`;
            } else if (domain === "r") {
                return svg`<g role="button" tabindex="0" aria-label="${t('profile.yourScore', { label: this.relationship_domain.label[3] })}" @click="${(e)=> { this.showTextMessage(e, this.relationship_domain.label[3]);}}" @keydown="${(e)=> { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.showTextMessage(e, this.relationship_domain.label[3]); } }}"><circle cx="170" cy="30" r="2" fill="black" /></g>`;
            }
        } else if (getScore(this.report[domain].average) === 5) {
            if (domain === "m") {
                return svg`<g role="button" tabindex="0" aria-label="${t('profile.yourScore', { label: this.message_domain.label[4] })}" @click="${(e)=> { this.showTextMessage(e, this.message_domain.label[4]);}}" @keydown="${(e)=> { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.showTextMessage(e, this.message_domain.label[4]); } }}"><circle cx="10" cy="10" r="2" fill="black" /></g>`;
            } else if (domain === "s") {
                return svg`<g role="button" tabindex="0" aria-label="${t('profile.yourScore', { label: this.space_domain.label[4] })}" @click="${(e)=> { this.showTextMessage(e, this.space_domain.label[4]);}}" @keydown="${(e)=> { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.showTextMessage(e, this.space_domain.label[4]); } }}"><circle cx="10" cy="190" r="2" fill="black" /></g>`;
            } else if (domain === "t") {
                return svg`<g role="button" tabindex="0" aria-label="${t('profile.yourScore', { label: this.time_domain.label[4] })}" @click="${(e)=> { this.showTextMessage(e, this.time_domain.label[4]);}}" @keydown="${(e)=> { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.showTextMessage(e, this.time_domain.label[4]); } }}"><circle cx="190" cy="190" r="2" fill="black" /></g>`;
            } else if (domain === "r") {
                return svg`<g role="button" tabindex="0" aria-label="${t('profile.yourScore', { label: this.relationship_domain.label[4] })}" @click="${(e)=> { this.showTextMessage(e, this.relationship_domain.label[4]);}}" @keydown="${(e)=> { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.showTextMessage(e, this.relationship_domain.label[4]); } }}"><circle cx="190" cy="10" r="2" fill="black" /></g>`;
            }
        }
        
    }
    
    // The chart plots four domains on two axes; this is its text equivalent
    // (WCAG 1.1.1), rendered visually hidden immediately before the graphic.
    describeProfile() {
        const domains = [
            ['m', this.message_domain],
            ['r', this.relationship_domain],
            ['t', this.time_domain],
            ['s', this.space_domain]
        ];
        const parts = domains
            .filter(([, d]) => d)
            .map(([key, d]) => {
                const score = getScore(this.report[key].average);
                return t('profile.describeDomain', { domain: d.name, score, label: d.label[score - 1] });
            });
        return t('profile.describe', { domains: parts.join('. ') });
    }

    render() {
        return html`
            <div id="clickable-div" role="status" aria-live="polite" @click=${(e)=>{ this.shadowRoot.getElementById('clickable-div').style.visibility = "hidden"; }}></div>

            <p class="sr-only">${this.describeProfile()}</p>

            <svg width="100%" height="100%" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"
                 role="group" aria-label=${t('profile.chartLabel')}>
                <rect width="200" height="200" stroke="white" stroke-width="1" fill="white" />
                <rect x="90" y="90" width="20" height="20" stroke="lightgray" stroke-width="1" fill="transparent" />
                <rect x="70" y="70" width="60" height="60" stroke="lightgray" stroke-width="1" fill="transparent" />
                <rect x="50" y="50" width="100" height="100" stroke="lightgray" stroke-width="1" fill="transparent" />
                <rect x="30" y="30" width="140" height="140" stroke="lightgray" stroke-width="1" fill="transparent" />
                <rect x="10" y="10" width="180" height="180" stroke="lightgray" stroke-width="1" fill="transparent" />
                <line x1="100" y1="0" x2="100" y2="200"  style="stroke:gray;stroke-width:2" />
                <line x1="0" y1="100" x2="200" y2="100"  style="stroke:gray;stroke-width:2" />
                ${this.svgDomainPoint("m", "1", this.message_domain.label[0], this.message_domain.color, 90, 90)}
                ${this.svgDomainPoint("m", "2", this.message_domain.label[1], this.message_domain.color, 70, 70)}
                ${this.svgDomainPoint("m", "3", this.message_domain.label[2], this.message_domain.color, 50, 50)}
                ${this.svgDomainPoint("m", "4", this.message_domain.label[3], this.message_domain.color, 30, 30)}
                ${this.svgDomainPoint("m", "5", this.message_domain.label[4], this.message_domain.color, 10, 10)}
                ${this.svgDomainPoint("s", "1", this.space_domain.label[0], this.space_domain.color, 90, 110)}
                ${this.svgDomainPoint("s", "2", this.space_domain.label[1], this.space_domain.color, 70, 130)}
                ${this.svgDomainPoint("s", "3", this.space_domain.label[2], this.space_domain.color, 50, 150)}
                ${this.svgDomainPoint("s", "4", this.space_domain.label[3], this.space_domain.color, 30, 170)}
                ${this.svgDomainPoint("s", "5", this.space_domain.label[4], this.space_domain.color, 10, 190)}
                ${this.svgDomainPoint("t", "1", this.time_domain.label[0], this.time_domain.color, 110, 110)}
                ${this.svgDomainPoint("t", "2", this.time_domain.label[1], this.time_domain.color, 130, 130)}
                ${this.svgDomainPoint("t", "3", this.time_domain.label[2], this.time_domain.color, 150, 150)}
                ${this.svgDomainPoint("t", "4", this.time_domain.label[3], this.time_domain.color, 170, 170)}
                ${this.svgDomainPoint("t", "5", this.time_domain.label[4], this.time_domain.color, 190, 190)}
                ${this.svgDomainPoint("r", "1", this.relationship_domain.label[0], this.relationship_domain.color, 110, 90)}
                ${this.svgDomainPoint("r", "2", this.relationship_domain.label[1], this.relationship_domain.color, 130, 70)}
                ${this.svgDomainPoint("r", "3", this.relationship_domain.label[2], this.relationship_domain.color, 150, 50)}
                ${this.svgDomainPoint("r", "4", this.relationship_domain.label[3], this.relationship_domain.color, 170, 30)}
                ${this.svgDomainPoint("r", "5", this.relationship_domain.label[4], this.relationship_domain.color, 190, 10)}
                ${this.svgDomainMyPoint("m")}
                ${this.svgDomainMyPoint("s")}
                ${this.svgDomainMyPoint("t")}
                ${this.svgDomainMyPoint("r")}               
            </svg>      `
    } 

}
if (!customElements.get('svg-profile')) customElements.define('svg-profile', SvgProfile);

export class SvgLegend extends Localized(LitElement) {
    static get styles() {
        return css`
            :host { display: block; width: 100%; max-width: 600px; margin: 10px auto; }
            .legend-row { display:flex; align-items:center; gap:12px; font-size: 14px; }
            .legend-label { min-width: 60px; text-align: right; font-weight: 600; }
        `;
    }
    static get properties() {
        return {
            rep: { type: Object },     // same shape as getSVGReport() output
            maxnum: { type: Number }   // same as group_report.num
        };
    }

    constructor() {
        super();
        this.rep = { m: {}, r: {}, s: {}, t: {} };
        this.maxnum = 5;
    }

    // Use the same scaling logic as SvgProfile.getSvgRatio
    getSvgRatio(n) {
        if (!n) return 1; // fallback minimum
        if (this.maxnum > 5) {
            const r = (n / this.maxnum) * 15;
            return r < 2 ? 1 : r;
        } else {
            return n < 2 ? 1 : n;
        }
    }

    // find numeric min and max across rep (all domains and textvalue keys)
    findMinMax() {
        let min = Infinity;
        let max = -Infinity;
        Object.keys(this.rep || {}).forEach(domain => {
            const domainObj = this.rep[domain] || {};
            Object.keys(domainObj).forEach(key => {
                const val = Number(domainObj[key]) || 0;
                if (val < min) min = val;
                if (val > max) max = val;
            });
        });
        // handle case where no points exist
        if (min === Infinity) min = 0;
        if (max === -Infinity) max = 0;
        return { min, max };
    }

    render() {
        const { min, max } = this.findMinMax();
        const minR = 3 + this.getSvgRatio(min);
        const maxR = 3 + this.getSvgRatio(max);

         //${(3 + this.getSvgRatio(this.rep[domain][textvalue]))}
        // simple spacing and labels
        return html`
                    <svg width="100%"  viewBox="0 0 200 40" xmlns="http://www.w3.org/2000/svg">
                        <!-- Lowest -->
                        <circle cx="24" cy="20" r="${minR}" fill="lightgray"></circle>
                        <text x="23" y="20" font-size="6" dominant-baseline="middle">${min}</text>
                        
                        <!-- Highest -->
                        <circle cx="24" cy="20" r="${maxR}" fill="lightgray" transform="translate(20,0)"></circle>
                        <text x="40" y="20" font-size="6" dominant-baseline="middle">${max}</text>

                        <text x="60" y="15" font-size="6" dominant-baseline="middle">
                             ${t('profile.legendCount')}
                        </text>
                        <text x="60" y="25" font-size="6" dominant-baseline="middle">
                             ${t('profile.legendDot')}
                        </text>
                    
                    </svg>
        `;
    }
}
if (!customElements.get('svg-legend')) customElements.define('svg-legend', SvgLegend);

